import React, { useState } from 'react';
import { Download, Facebook, Link2, MessageCircle, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const TITLES = {
  flag: { en: 'The NDP Flag — Its Significance', bn: 'এনডিপির পতাকা — তাৎপর্য' },
  tiger: { en: 'The NDP Tiger — Its Significance', bn: 'এনডিপির বাঘ — তাৎপর্য' },
  roundel: { en: 'The NDP Roundel — Its Significance', bn: 'এনডিপির বৃত্ত-প্রতীক — তাৎপর্য' },
};

export const ShareBar = ({ symbol, dark = false }) => {
  const { t, language } = useLanguage();
  const [busy, setBusy] = useState(false);
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/${symbol}?lang=${language}`;
  const card = `/images/share/${symbol}_${language}.jpg`;
  const title = t(TITLES[symbol]);
  const btn = `inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${dark ? 'bg-white/10 text-white hover:bg-white/20' : 'border border-gray-200 bg-white text-gray-700 hover:border-party-green hover:text-party-green'}`;
  const open = (href) => window.open(href, '_blank', 'noopener,noreferrer,width=640,height=560');
  const shareImage = async () => {
    setBusy(true);
    try {
      const blob = await (await fetch(card)).blob();
      const file = new File([blob], `NDP_${symbol}_${language}.jpg`, { type: 'image/jpeg' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title, text: `${title} ${url}` });
      } else {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success(t({ en: 'Card downloaded — attach it to your post', bn: 'কার্ড ডাউনলোড হয়েছে — পোস্টে যুক্ত করুন' }));
      }
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error(t({ en: 'Could not share the card', bn: 'কার্ড শেয়ার করা যায়নি' }));
    }
    setBusy(false);
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success(t({ en: 'Link copied', bn: 'লিংক কপি হয়েছে' })); } catch { toast.error(url); }
  };
  return (
    <div className="mt-6 flex flex-wrap items-center gap-2" data-testid={`share-bar-${symbol}${dark ? '-bottom' : ''}`}>
      <span className={`mr-1 text-sm font-medium ${dark ? 'text-gray-300' : 'text-gray-500'}`}>{t({ en: 'Share', bn: 'শেয়ার করুন' })}</span>
      <button type="button" className={btn} onClick={() => open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`)} data-testid={`share-facebook-${symbol}${dark ? '-bottom' : ''}`}><Facebook className="h-4 w-4" />Facebook</button>
      <button type="button" className={btn} onClick={() => open(`https://wa.me/?text=${encodeURIComponent(`${title}\n${url}`)}`)} data-testid={`share-whatsapp-${symbol}${dark ? '-bottom' : ''}`}><MessageCircle className="h-4 w-4" />WhatsApp</button>
      <button type="button" className={btn} onClick={shareImage} disabled={busy} data-testid={`share-card-${symbol}${dark ? '-bottom' : ''}`}>
        {navigator.canShare ? <Share2 className="h-4 w-4" /> : <Download className="h-4 w-4" />}{t({ en: 'Share card image', bn: 'কার্ডের ছবি শেয়ার' })}
      </button>
      <button type="button" className={btn} onClick={copy} aria-label={t({ en: 'Copy link', bn: 'লিংক কপি' })} data-testid={`share-copy-${symbol}${dark ? '-bottom' : ''}`}><Link2 className="h-4 w-4" /></button>
    </div>
  );
};
